/**
 * Location Tracking Components for Command Centre
 * Breadcrumb trails, replay controls, and officer tracking details.
 */
import React, { useState, useEffect, useCallback } from 'react';
import { Polyline, CircleMarker, Popup } from 'react-leaflet';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Slider } from '../components/ui/slider';
import {
  Play, Pause, SkipBack, SkipForward, MapPin, Clock, Battery,
  Navigation, Route, AlertTriangle, CheckCircle, Activity,
  Footprints, Building2
} from 'lucide-react';
import OfficerAvatar from '@/components/common/OfficerAvatar';

// Trail colors by movement state
const TRAIL_COLORS = {
  moving: '#22c55e',    // Green
  stationary: '#eab308', // Yellow
  unknown: '#6b7280',    // Gray
};

/**
 * BreadcrumbTrail - Renders polyline trail on map
 */
export function BreadcrumbTrail({ locations, showPoints = true }) {
  if (!locations || locations.length < 2) return null;

  // Create polyline positions
  const positions = locations.map(loc => [loc.latitude, loc.longitude]);

  // Create colored segments based on movement state
  const segments = [];
  for (let i = 1; i < locations.length; i++) {
    const prevLoc = locations[i - 1];
    const currLoc = locations[i];
    const color = TRAIL_COLORS[currLoc.movement_state] || TRAIL_COLORS.unknown;
    
    segments.push({
      positions: [
        [prevLoc.latitude, prevLoc.longitude],
        [currLoc.latitude, currLoc.longitude]
      ],
      color,
      timestamp: currLoc.timestamp,
    });
  }

  return (
    <>
      {/* Main trail polyline */}
      <Polyline
        positions={positions}
        pathOptions={{
          color: '#3b82f6',
          weight: 3,
          opacity: 0.7,
          dashArray: '5, 10',
        }}
      />

      {/* Colored segments */}
      {segments.map((seg, idx) => (
        <Polyline
          key={`seg-${idx}`}
          positions={seg.positions}
          pathOptions={{
            color: seg.color,
            weight: 4,
            opacity: 0.9,
          }}
        />
      ))}

      {/* Location points */}
      {showPoints && locations.map((loc, idx) => (
        <CircleMarker
          key={`point-${idx}`}
          center={[loc.latitude, loc.longitude]}
          radius={idx === locations.length - 1 ? 8 : 4}
          pathOptions={{
            color: idx === locations.length - 1 ? '#ef4444' : TRAIL_COLORS[loc.movement_state] || '#6b7280',
            fillColor: idx === locations.length - 1 ? '#ef4444' : TRAIL_COLORS[loc.movement_state] || '#6b7280',
            fillOpacity: idx === locations.length - 1 ? 1 : 0.6,
            weight: idx === locations.length - 1 ? 3 : 1,
          }}
        >
          <Popup>
            <div className="text-xs">
              <div className="font-bold">{formatTime(loc.timestamp)}</div>
              <div>Speed: {loc.speed?.toFixed(1) || '?'} m/s</div>
              <div>Battery: {loc.battery_level || '?'}%</div>
              <div className="capitalize">State: {loc.movement_state}</div>
            </div>
          </Popup>
        </CircleMarker>
      ))}
    </>
  );
}

/**
 * ReplayControls - Playback controls for trail replay
 */
export function ReplayControls({ 
  locations, 
  currentIndex, 
  onIndexChange,
  isPlaying,
  onPlayPause,
  playbackSpeed,
  onSpeedChange
}) {
  const totalPoints = locations?.length || 0;
  const currentLoc = locations?.[currentIndex];

  return (
    <Card className="bg-[#18181b] border-[#3f3f46]">
      <CardHeader className="pb-2">
        <CardTitle className="text-sm font-rajdhani flex items-center gap-2">
          <Route className="w-4 h-4 text-blue-400" />
          TRAIL REPLAY
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Timeline slider */}
        <div className="space-y-2">
          <Slider
            value={[currentIndex]}
            max={Math.max(0, totalPoints - 1)}
            step={1}
            onValueChange={([val]) => onIndexChange(val)}
            className="w-full"
          />
          <div className="flex justify-between text-xs text-zinc-500">
            <span>{locations?.[0]?.timestamp ? formatTime(locations[0].timestamp) : '--:--'}</span>
            <span>{currentLoc?.timestamp ? formatTime(currentLoc.timestamp) : '--:--'}</span>
            <span>{locations?.[totalPoints - 1]?.timestamp ? formatTime(locations[totalPoints - 1].timestamp) : '--:--'}</span>
          </div>
        </div>

        {/* Playback controls */}
        <div className="flex items-center justify-center gap-2">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => onIndexChange(0)}
            disabled={totalPoints === 0}
          >
            <SkipBack className="w-4 h-4" />
          </Button>
          <Button
            variant="default"
            size="sm"
            onClick={onPlayPause}
            disabled={totalPoints === 0}
            className="px-4"
          >
            {isPlaying ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4" />}
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => onIndexChange(totalPoints - 1)}
            disabled={totalPoints === 0}
          >
            <SkipForward className="w-4 h-4" />
          </Button>
        </div>

        {/* Speed control */}
        <div className="flex items-center gap-2 text-xs">
          <span className="text-zinc-500">Speed:</span>
          <Button
            variant={playbackSpeed === 1 ? 'default' : 'ghost'}
            size="sm"
            className="h-6 px-2 text-xs"
            onClick={() => onSpeedChange(1)}
          >
            1x
          </Button>
          <Button
            variant={playbackSpeed === 2 ? 'default' : 'ghost'}
            size="sm"
            className="h-6 px-2 text-xs"
            onClick={() => onSpeedChange(2)}
          >
            2x
          </Button>
          <Button
            variant={playbackSpeed === 5 ? 'default' : 'ghost'}
            size="sm"
            className="h-6 px-2 text-xs"
            onClick={() => onSpeedChange(5)}
          >
            5x
          </Button>
        </div>

        {/* Current point info */}
        {currentLoc && (
          <div className="text-xs bg-[#09090b] p-2 rounded space-y-1">
            <div className="flex justify-between">
              <span className="text-zinc-500">Time:</span>
              <span className="text-white">{formatTime(currentLoc.timestamp)}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-zinc-500">Coords:</span>
              <span className="text-white font-mono">
                {currentLoc.latitude.toFixed(5)}, {currentLoc.longitude.toFixed(5)}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-zinc-500">Speed:</span>
              <span className="text-white">{currentLoc.speed?.toFixed(1) || '?'} m/s</span>
            </div>
            <div className="flex justify-between">
              <span className="text-zinc-500">State:</span>
              <Badge 
                variant="outline" 
                className={`text-[10px] ${
                  currentLoc.movement_state === 'moving' ? 'border-green-500 text-green-500' :
                  currentLoc.movement_state === 'stationary' ? 'border-yellow-500 text-yellow-500' :
                  'border-zinc-500 text-zinc-500'
                }`}
              >
                {currentLoc.movement_state}
              </Badge>
            </div>
          </div>
        )}

        <div className="text-xs text-zinc-500 text-center">
          Point {currentIndex + 1} of {totalPoints}
        </div>
      </CardContent>
    </Card>
  );
}

/**
 * OfficerTrackingDetails - Enhanced officer info panel with tracking data
 */
export function OfficerTrackingDetails({ 
  officer, 
  trackingData,
  siteVisits,
  onViewTrail,
  isLoading
}) {
  if (isLoading) {
    return (
      <Card className="bg-[#18181b] border-[#3f3f46]">
        <CardContent className="py-8 text-center">
          <Activity className="w-6 h-6 animate-spin mx-auto text-blue-400" />
          <p className="text-zinc-500 mt-2">Loading tracking data...</p>
        </CardContent>
      </Card>
    );
  }

  const lastPing = trackingData?.last_ping;
  const isStale = trackingData?.is_stale;
  const stats = trackingData?.stats;

  return (
    <Card className="bg-[#18181b] border-[#3f3f46]">
      <CardHeader className="pb-2">
        <CardTitle className="text-sm font-rajdhani flex items-center gap-2">
          <Footprints className="w-4 h-4 text-emerald-400" />
          OFFICER TRACKING
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Officer info */}
        <div className="flex items-center gap-3">
          <OfficerAvatar
            user={officer}
            size="md"
            showRing={false}
            className="ring-2 ring-emerald-500/60 shadow-sm"
          />
          <div>
            <div className="font-semibold text-white">{officer?.name}</div>
            <div className="text-xs text-zinc-500">{officer?.site_name}</div>
          </div>
        </div>

        {/* Tracking status */}
        <div className="flex items-center gap-2">
          {trackingData?.is_tracking ? (
            <Badge className="bg-emerald-500/20 text-emerald-400 border-emerald-500/50">
              <Activity className="w-3 h-3 mr-1" />
              Tracking Active
            </Badge>
          ) : (
            <Badge className="bg-zinc-500/20 text-zinc-400 border-zinc-500/50">
              No Tracking Data
            </Badge>
          )}
          
          {isStale && (
            <Badge className="bg-yellow-500/20 text-yellow-400 border-yellow-500/50">
              <AlertTriangle className="w-3 h-3 mr-1" />
              Stale GPS
            </Badge>
          )}
        </div>

        {/* Last ping info */}
        {lastPing && (
          <div className="bg-[#09090b] rounded p-3 space-y-2">
            <div className="text-xs font-semibold text-zinc-400 uppercase">Last Update</div>
            <div className="grid grid-cols-2 gap-2 text-xs">
              <div className="flex items-center gap-1">
                <Clock className="w-3 h-3 text-zinc-500" />
                <span className="text-white">{formatTimeAgo(lastPing.timestamp)}</span>
              </div>
              <div className="flex items-center gap-1">
                <Navigation className="w-3 h-3 text-zinc-500" />
                <span className="text-white capitalize">{lastPing.movement_state || 'unknown'}</span>
              </div>
              {lastPing.battery_level && (
                <div className="flex items-center gap-1">
                  <Battery className="w-3 h-3 text-zinc-500" />
                  <span className={`${lastPing.battery_level < 20 ? 'text-red-400' : 'text-white'}`}>
                    {lastPing.battery_level}%
                  </span>
                </div>
              )}
              {lastPing.speed !== null && lastPing.speed !== undefined && (
                <div className="flex items-center gap-1">
                  <Activity className="w-3 h-3 text-zinc-500" />
                  <span className="text-white">{lastPing.speed.toFixed(1)} m/s</span>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Stats */}
        {stats && (
          <div className="grid grid-cols-2 gap-2">
            <div className="bg-[#09090b] rounded p-2 text-center">
              <div className="text-lg font-bold text-blue-400">{stats.total_distance_km}</div>
              <div className="text-[10px] text-zinc-500">KM TRAVELLED</div>
            </div>
            <div className="bg-[#09090b] rounded p-2 text-center">
              <div className="text-lg font-bold text-emerald-400">{stats.visited_sites_count}</div>
              <div className="text-[10px] text-zinc-500">SITES VISITED</div>
            </div>
          </div>
        )}

        {/* Site visits */}
        {siteVisits && siteVisits.length > 0 && (
          <div className="space-y-2">
            <div className="text-xs font-semibold text-zinc-400 uppercase flex items-center gap-1">
              <Building2 className="w-3 h-3" />
              Site Visits
            </div>
            <div className="space-y-1 max-h-[150px] overflow-y-auto">
              {siteVisits.map((visit, idx) => (
                <div 
                  key={visit.id || idx}
                  className="bg-[#09090b] rounded p-2 text-xs flex items-center justify-between"
                >
                  <div className="flex items-center gap-2">
                    {visit.is_current ? (
                      <div className="w-2 h-2 bg-emerald-500 rounded-full animate-pulse" />
                    ) : (
                      <CheckCircle className="w-3 h-3 text-emerald-500" />
                    )}
                    <span className="text-white">{visit.site_name}</span>
                  </div>
                  <div className="text-zinc-500">
                    {visit.dwell_time_formatted || (visit.is_current ? 'Now' : '--')}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* View trail button */}
        <Button
          onClick={onViewTrail}
          className="w-full bg-blue-600 hover:bg-blue-700"
          disabled={!trackingData?.is_tracking}
        >
          <Route className="w-4 h-4 mr-2" />
          View Breadcrumb Trail
        </Button>
      </CardContent>
    </Card>
  );
}

/**
 * TrackingModeToggle - Toggle between Live/Trail/Replay modes
 */
export function TrackingModeToggle({ mode, onModeChange }) {
  return (
    <div className="flex items-center gap-1 bg-[#09090b] rounded p-1">
      <Button
        variant={mode === 'live' ? 'default' : 'ghost'}
        size="sm"
        className="h-7 px-3 text-xs"
        onClick={() => onModeChange('live')}
      >
        <Activity className="w-3 h-3 mr-1" />
        Live
      </Button>
      <Button
        variant={mode === 'trail' ? 'default' : 'ghost'}
        size="sm"
        className="h-7 px-3 text-xs"
        onClick={() => onModeChange('trail')}
      >
        <Route className="w-3 h-3 mr-1" />
        Trail
      </Button>
      <Button
        variant={mode === 'replay' ? 'default' : 'ghost'}
        size="sm"
        className="h-7 px-3 text-xs"
        onClick={() => onModeChange('replay')}
      >
        <Play className="w-3 h-3 mr-1" />
        Replay
      </Button>
    </div>
  );
}

// Helper functions
function formatTime(isoString) {
  if (!isoString) return '--:--';
  try {
    const date = new Date(isoString);
    return date.toLocaleTimeString('en-AU', { hour: '2-digit', minute: '2-digit', second: '2-digit' });
  } catch {
    return '--:--';
  }
}

function formatTimeAgo(isoString) {
  if (!isoString) return 'Never';
  try {
    const date = new Date(isoString);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    
    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    const diffHours = Math.floor(diffMins / 60);
    if (diffHours < 24) return `${diffHours}h ago`;
    const diffDays = Math.floor(diffHours / 24);
    return `${diffDays}d ago`;
  } catch {
    return 'Unknown';
  }
}

export default {
  BreadcrumbTrail,
  ReplayControls,
  OfficerTrackingDetails,
  TrackingModeToggle,
};
